import React from 'react';
import { Controller } from 'react-hook-form';
import { Input, Typography } from 'antd';
import { AddressBox, ErrorText, FieldGroup, FieldLabel, FieldRow, FlexField, HintText } from './styles';
import type { AddressData, AddressFieldsProps } from './types';

const { Title } = Typography;

const AddressFields: React.FC<AddressFieldsProps> = ({ control, isDarkMode, onFieldChange, onPhoneChange, errors }) => {
  const field = (name: keyof AddressData, placeholder: string, error?: string) => (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: f }) => (
          <Input
            {...f}
            placeholder={placeholder}
            status={error ? 'error' : ''}
            onChange={(e) => { f.onChange(e); onFieldChange(name, e.target.value); }}
          />
        )}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AddressBox $isDark={isDarkMode}>
        <Title level={5} style={{ margin: 0 }}>Address Details</Title>

        <FieldRow>
          <FlexField $flex={1}>
            <FieldLabel $isDark={isDarkMode}>Street Number <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            {field('streetNumber', '123', errors?.streetNumber)}
          </FlexField>
          <FlexField $flex={3}>
            <FieldLabel $isDark={isDarkMode}>Street Name <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            {field('streetName', 'Main Street', errors?.streetName)}
          </FlexField>
          <FlexField $flex={1}>
            <FieldLabel $isDark={isDarkMode}>Apt/Unit <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            {field('apartment', '3B', errors?.apartment)}
          </FlexField>
        </FieldRow>

        <FieldRow>
          <FlexField $flex={2}>
            <FieldLabel $isDark={isDarkMode}>City <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            {field('city', 'New York', errors?.city)}
          </FlexField>
          <FlexField $flex={1}>
            <FieldLabel $isDark={isDarkMode}>State <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            {field('state', 'NY', errors?.state)}
          </FlexField>
          <FlexField $flex={1}>
            <FieldLabel $isDark={isDarkMode}>ZIP Code <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
            {field('zipCode', '10001', errors?.zipCode)}
          </FlexField>
        </FieldRow>

        <FieldGroup>
          <FieldLabel $isDark={isDarkMode}>Full Address <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
          {field('address', 'Complete address will appear here', errors?.address)}
          <HintText type="secondary">
            Automatically filled by the address search above, but you can edit it manually.
          </HintText>
        </FieldGroup>
      </AddressBox>

      <FieldGroup>
        <FieldLabel $isDark={isDarkMode}>Phone Number <span style={{ color: '#ff4d4f' }}>*</span></FieldLabel>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field: f }) => (
            <Input
              {...f}
              placeholder="(555) 123-4567"
              maxLength={14}
              status={errors?.phoneNumber ? 'error' : ''}
              onChange={(e) => { f.onChange(e); onPhoneChange(e); }}
            />
          )}
        />
        {errors?.phoneNumber && <ErrorText>{errors.phoneNumber}</ErrorText>}
      </FieldGroup>
    </div>
  );
};

export default AddressFields;
